import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import type {
  ImportStatus, MappingStatus, RunStatus, SourceRunStatus,
} from '@/api/hrms-sync';

export function SourceStatusIcon({ status }: { status: SourceRunStatus | undefined }) {
  const sx = { fontSize: 18 };
  switch (status) {
    case 'OK':
      return <CheckCircleIcon color="success" sx={sx} />;
    case 'PARTIAL':
      return <WarningAmberIcon color="warning" sx={sx} />;
    case 'FAILED':
      return <ErrorOutlineIcon color="error" sx={sx} />;
    case 'SKIPPED':
      return <RemoveCircleOutlineIcon color="disabled" sx={sx} />;
    case 'RUNNING':
      return <CircularProgress size={15} />;
    default:
      return <RadioButtonUncheckedIcon color="disabled" sx={sx} />;
  }
}

const RUN_COLOR: Record<RunStatus, 'default' | 'info' | 'success' | 'warning' | 'error'> = {
  QUEUED: 'info',
  RUNNING: 'info',
  CANCELLING: 'warning',
  COMPLETED: 'success',
  COMPLETED_WITH_ERRORS: 'warning',
  FAILED: 'error',
  CANCELLED: 'default',
};

const RUN_LABEL: Record<RunStatus, string> = {
  QUEUED: 'Queued',
  RUNNING: 'Running',
  CANCELLING: 'Stopping',
  COMPLETED: 'Completed',
  COMPLETED_WITH_ERRORS: 'Completed with errors',
  FAILED: 'Failed',
  CANCELLED: 'Cancelled',
};

export function RunStatusChip({ status }: { status: RunStatus }) {
  return (
    <Chip size="small" color={RUN_COLOR[status] ?? 'default'}
      label={RUN_LABEL[status] ?? status} sx={{ fontWeight: 600 }} />
  );
}

const MAPPING: Record<MappingStatus, { label: string; color: 'default' | 'success' | 'info' | 'warning'; tip: string }> = {
  none: { label: 'Store only', color: 'default', tip: 'Stored in its own table; not imported into a payroll master.' },
  confirmed: { label: 'Confirmed', color: 'success', tip: 'Imported into payroll on every sync using the confirmed mapping.' },
  auto_ready: { label: 'Auto-matched', color: 'info', tip: 'Every required column matched with high confidence. Confirm it to import on dashboard syncs.' },
  needs_review: { label: 'Needs mapping', color: 'warning', tip: 'Some required columns could not be matched. Map them to import this data.' },
};

export function MappingChip({ status }: { status: MappingStatus }) {
  const m = MAPPING[status];
  return (
    <Tooltip title={m.tip}>
      <Chip size="small" variant={status === 'none' ? 'outlined' : 'filled'}
        color={m.color} label={m.label} />
    </Tooltip>
  );
}

const IMPORT: Record<ImportStatus, { label: string; color: 'default' | 'success' | 'warning' | 'error' }> = {
  IMPORTED: { label: 'Imported', color: 'success' },
  STORED_ONLY: { label: 'Stored', color: 'default' },
  NEEDS_MAPPING: { label: 'Needs mapping', color: 'warning' },
  BLOCKED: { label: 'Blocked', color: 'warning' },
  FAILED: { label: 'Import failed', color: 'error' },
  SKIPPED: { label: 'Skipped', color: 'default' },
};

export function ImportChip({ status }: { status: ImportStatus | undefined }) {
  if (!status) return null;
  const m = IMPORT[status] ?? { label: status, color: 'default' as const };
  return <Chip size="small" variant="outlined" color={m.color} label={m.label} />;
}

export function ConfidenceChip({ value }: { value: 'high' | 'medium' | 'low' | null }) {
  if (!value) return null;
  const color = value === 'high' ? 'success' : value === 'medium' ? 'warning' : 'error';
  return <Chip size="small" variant="outlined" color={color} label={value} sx={{ height: 20 }} />;
}
